import React, { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsComparedGroupedAggregate, SalesInsightsGroupedAggregateItem, useSalesInsightsCompareGroupedAggregated } from '../../../../api/indicators';
import { dateRange, formatDateParam, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';
import { DashboardContext } from '../../DashboardContext';
import { UserContext } from '../../../../context/UserContext';
import LineBarChartCompared from '../charts/lineBarChart/LineBarChartCompared';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromComparedRanges } from '../../dashboardHelpers';

const ComparedNetSalesByLocation = () => {
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);

    const initialStartDate = filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialEndDate = filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    const initialCompareStart = filters.comparePeriodFrom
        ? filters.comparePeriodFrom
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialCompareEnd = filters.comparePeriodTo
        ? filters.comparePeriodTo
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netSales',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        compareStartDate: formatDateParam(initialCompareStart),
        compareEndDate: formatDateParam(initialCompareEnd),
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromComparedRanges(
            initialStartDate,
            initialEndDate,
            initialCompareStart,
            initialCompareEnd
        ),
        groupBy: 'location'
    });

    const {
        data: comparedGroupedAggregateSalesInsightsResponse,
        isLoading,
        isError,
        refetch: fetchNetSalesByLocation
    } = useSalesInsightsCompareGroupedAggregated<SalesInsightsComparedGroupedAggregate>(params, 'compared-net-sales-by-location/grouped/aggregate')

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        const compareStart = filters.comparePeriodFrom
            ? filters.comparePeriodFrom
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

        const compareEnd = filters.comparePeriodTo
            ? filters.comparePeriodTo
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

        const bucket = getBucketFromComparedRanges(
            start,
            end,
            compareStart,
            compareEnd
        );
        setParams(p => ({
            ...p,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            compareStartDate: formatDateParam(compareStart),
            compareEndDate: formatDateParam(compareEnd),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket
        }));
    }, [filters, dateRangeConfig]);

    useEffect(() => { fetchNetSalesByLocation(); }, [params, fetchNetSalesByLocation]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchNetSalesByLocation();
        }
    }, [refreshDate, fetchNetSalesByLocation]);

    function transformData(dataItem: SalesInsightsGroupedAggregateItem): LineBarChartDataItem {
        const result: LineBarChartDataItem = {
            dateTime: new Date(dataItem.dateTime),
            barData: 0
        };

        dataItem.salesInsightsGrouped.forEach(({ key, salesInsights }) => {
            result[key] = salesInsights.netSalesExcTax ?? 0;
            result.barData += salesInsights.transactionVolume ?? 0;
        });
        return result;
    }

    return (
        <Box>
            <LineBarChartCompared
                title={t('screens.dashboarding.charts.netSalesByLocation')}
                description={t('screens.dashboarding.charts.excludingTax')}
                currency={comparedGroupedAggregateSalesInsightsResponse?.currency}
                isError={isError}
                isLoading={isLoading}
                leftAxisLabel={t("screens.dashboarding.charts.amount")}
                rightAxisLabel={t("screens.dashboarding.charts.volume")}
                barLabel={t("screens.dashboarding.charts.volume")}
                data={comparedGroupedAggregateSalesInsightsResponse?.salesInsightsGroupedAggregate?.map(transformData) ?? []}
                dataCompared={comparedGroupedAggregateSalesInsightsResponse?.comparedSalesInsightsGroupedAggregate?.map(transformData) ?? []}
                bucket={params.bucket as Bucket}
                setBucket={(newView: Bucket) => setParams({ ...params, bucket: newView })}
                height={560}
                currentDateFrom={filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
            />
        </Box>
    )
}

export default ComparedNetSalesByLocation;
